import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  CreditCardIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { formatPrice } from '../../utils/localeConfig';
import VariableCreditPurchase from './VariableCreditPurchase';
import {
  initializePaddle,
  openPaddleCheckout,
  getPaddlePriceId
} from '../../utils/paddle';
import {
  trackCreditPurchaseView,
  trackCreditCheckoutBegin,
  trackCreditPurchase,
  trackCreditPurchaseAbandonment,
  trackPaymentError
} from '../../utils/analytics';
import Logger from '../../utils/logger';

const CreditPurchaseModal = ({ isOpen, onClose, onPaymentSuccess }) => {
  const { currentUser } = useAuth();
  const { locale, loading: localeLoading } = useLocale();
  const [pricingData, setPricingData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalOpenTime, setModalOpenTime] = useState(null);

  // Determine payment provider based on country
  const isSouthAfrica = locale?.code === 'ZA';
  const paymentProvider = isSouthAfrica ? 'Paystack' : 'Paddle';

  // Initialize Paddle for international users
  useEffect(() => {
    if (isOpen && !isSouthAfrica) {
      initializePaddle();
    }
  }, [isOpen, isSouthAfrica]);

  // Debug component state
  Logger.debug('CreditPurchaseModal render', {
    isOpen,
    isProcessing,
    hasPricingData: !!pricingData,
    pricingValid: pricingData?.isValid,
    currentUser: currentUser ? 'exists' : 'missing'
  });

  // Track modal view when opened and handle cleanup
  useEffect(() => {
    if (isOpen) {
      setModalOpenTime(Date.now());
      trackCreditPurchaseView({
        user_id: currentUser?.uid,
        modal_opened: new Date().toISOString()
      });
    } else {
      // When modal closes, reset processing state and track abandonment if needed
      if (modalOpenTime) {
        const timeSpent = Math.floor((Date.now() - modalOpenTime) / 1000);
        // Only track abandonment if we have valid pricing data
        // (successful payments are tracked separately in the payment callback)
        if (pricingData?.isValid) {
          trackCreditPurchaseAbandonment('modal_closed', {
            quantity: pricingData.quantity,
            totalPrice: pricingData.totalPrice,
            timeSpent: timeSpent
          });
        }
        setModalOpenTime(null);
      }
      // Always reset processing state when modal closes
      setIsProcessing(false);
    }
  }, [isOpen, currentUser?.uid]);

  // Safety cleanup: Reset processing state if stuck for too long
  useEffect(() => {
    if (isProcessing) {
      const timeout = setTimeout(() => {
        Logger.warn('Processing state stuck, auto-resetting...');
        setIsProcessing(false);
      }, 30000); // 30 seconds timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isProcessing]);

  // Handle pricing changes from VariableCreditPurchase component
  const handlePricingChange = useCallback((pricing) => {
    setPricingData(pricing);
  }, []);

  const handlePaystackPayment = async () => {
    Logger.payment('Payment process started');
    Logger.debug('Pricing data:', pricingData);
    
    if (!pricingData || !pricingData.isValid) {
      Logger.payment('Error - Invalid pricing data:', pricingData);
      alert('Please select a valid credit amount.');
      return;
    }
    
    const packageInfo = {
      id: `variable_${pricingData.quantity}`,
      credits: pricingData.quantity,
      price: pricingData.totalPrice,
      pricePerCredit: pricingData.pricePerCredit
    };
    
    Logger.debug('Package info created:', packageInfo);
    
    // Track checkout begin
    trackCreditCheckoutBegin({
      quantity: pricingData.quantity,
      totalPrice: pricingData.totalPrice,
      pricingTier: getTierName(pricingData.pricePerCredit)
    });
    
    Logger.debug('Setting processing state to true');
    setIsProcessing(true);
    
    try {
      // Validate environment variables
      Logger.debug('Checking environment variables...');
      const paystackPublicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
      Logger.debug('Paystack key exists:', !!paystackPublicKey);
      Logger.debug('Paystack key prefix:', paystackPublicKey ? paystackPublicKey.substring(0, 8) + '...' : 'none');
      
      if (!paystackPublicKey || paystackPublicKey === 'your_paystack_public_key_here') {
        Logger.payment('Error - Paystack public key validation failed');
        throw new Error('Paystack public key not configured');
      }

      // Import Paystack library dynamically
      Logger.debug('Loading Paystack library...');
      const PaystackPop = (await import('@paystack/inline-js')).default;
      Logger.debug('Paystack library loaded successfully:', !!PaystackPop);
      
      Logger.debug('Creating Paystack instance...');
      const paystack = new PaystackPop();
      Logger.debug('Paystack instance created:', !!paystack);
      
      // Generate unique reference
      const reference = `credits_${currentUser.uid.substring(0, 8)}_${Date.now()}`;
      Logger.debug('Payment reference generated:', reference);
      
      const config = {
        key: paystackPublicKey,
        email: currentUser.email,
        amount: packageInfo.price * 100, // Convert to cents (ZAR)
        currency: 'ZAR',
        ref: reference,
        metadata: {
          userId: currentUser.uid,
          credits: packageInfo.credits,
          packageId: packageInfo.id,
          userEmail: currentUser.email
        },
        callback: function(response) {
          Logger.payment('Paystack payment successful:', response);
          Logger.debug('Payment response details:', {
            reference: response.reference,
            status: response.status,
            transaction: response.transaction
          });
          
          // Track successful purchase
          trackCreditPurchase({
            transactionId: response.reference,
            quantity: packageInfo.credits,
            totalPrice: packageInfo.price,
            pricingTier: getTierName(packageInfo.pricePerCredit),
            pricePerCredit: packageInfo.pricePerCredit,
            paymentMethod: 'paystack',
            savings: calculateSavings(packageInfo.credits, packageInfo.pricePerCredit),
            userId: currentUser.uid
          });
          
          Logger.debug('Calling onPaymentSuccess...');
          // Handle successful payment
          onPaymentSuccess({
            reference: response.reference,
            amount: packageInfo.price * 100, // Keep in cents for consistency
            metadata: {
              credits: packageInfo.credits,
              packageId: packageInfo.id
            },
            status: response.status,
            transaction: response.transaction
          });
          
          Logger.payment('Payment success handling completed');
          // Reset processing state after successful payment
          setIsProcessing(false);
        },
        onClose: function() {
          Logger.payment('Paystack payment modal closed by user');
          Logger.debug('Modal close - package info:', packageInfo);
          
          // Track payment abandonment
          if (packageInfo) {
            trackCreditPurchaseAbandonment('payment_modal_closed', {
              quantity: packageInfo.credits,
              totalPrice: packageInfo.price
            });
          }
          
          Logger.debug('Resetting processing state due to modal close');
          setIsProcessing(false);
        }
      };

      Logger.debug('Initializing Paystack with config:', {
        email: config.email,
        amount: config.amount,
        currency: config.currency,
        ref: config.ref,
        keyPrefix: config.key ? config.key.substring(0, 8) + '...' : 'none',
        metadataKeys: Object.keys(config.metadata)
      });

      Logger.debug('About to call paystack.newTransaction...');
      paystack.newTransaction(config);
      Logger.debug('paystack.newTransaction called successfully');
      
    } catch (error) {
      Logger.payment('Error - Payment initialization error:', error);
      Logger.error('Payment error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      Logger.debug('Context when error occurred:', {
        packageInfo,
        paystackPublicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY ? 'exists' : 'missing',
        currentUser: currentUser ? 'exists' : 'missing'
      });
      
      // Always ensure processing state is reset on error
      Logger.debug('Resetting processing state due to error');
      setIsProcessing(false);
      
      // Track payment error
      trackPaymentError({
        errorType: 'payment_initialization_error',
        errorMessage: error.message || 'Payment initialization failed',
        amount: packageInfo.price,
        paymentMethod: 'paystack',
        transactionId: `failed_${Date.now()}`
      });
      
      // More specific error messages
      if (error.message.includes('public key')) {
        Logger.payment('Error - Public key configuration error');
        alert('Payment configuration error. Please contact support.');
      } else if (error.message.includes('import')) {
        Logger.payment('Error - Paystack library import error');
        alert('Payment system not loaded. Please refresh and try again.');
      } else {
        Logger.payment('Error - Generic payment error:', error.message);
        alert(`Payment failed: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  const handlePaddlePayment = async () => {
    Logger.payment('Paddle payment process started');
    Logger.debug('Pricing data:', pricingData);

    if (!pricingData || !pricingData.isValid) {
      Logger.payment('Error - Invalid pricing data:', pricingData);
      alert('Please select a valid credit package.');
      return;
    }

    // Track checkout begin
    trackCreditCheckoutBegin({
      quantity: pricingData.quantity,
      totalPrice: pricingData.totalPrice,
      country: locale.code,
      currency: locale.currency.code
    });

    Logger.debug('Setting processing state to true');
    setIsProcessing(true);

    try {
      // Get Price ID for selected quantity (no country needed - Paddle handles localization)
      const priceId = getPaddlePriceId(pricingData.quantity);

      Logger.debug('Opening Paddle checkout:', {
        priceId,
        credits: pricingData.quantity,
        country: locale.code
      });

      await openPaddleCheckout({
        priceId: priceId,
        userId: currentUser.uid,
        email: currentUser.email,
        credits: pricingData.quantity,
        countryCode: locale.code,
        successCallback: (data) => {
          Logger.payment('Paddle payment successful:', data);

          trackCreditPurchase({
            transactionId: data.transaction_id,
            quantity: pricingData.quantity,
            country: locale.code,
            paymentMethod: 'paddle',
            userId: currentUser.uid
          });

          onPaymentSuccess({
            provider: 'paddle',
            transaction_id: data.transaction_id,
            credits: pricingData.quantity,
            currency: locale.currency.code
          });

          setIsProcessing(false);

          // Wait for Paddle overlay to fully close before closing modal
          setTimeout(() => {
            onClose();
          }, 500);
        },
        closeCallback: () => {
          Logger.payment('Paddle checkout closed');
          setIsProcessing(false);
        }
      });

    } catch (error) {
      Logger.payment('Error - Paddle payment error:', error);
      setIsProcessing(false);

      trackPaymentError({
        errorType: 'paddle_error',
        errorMessage: error.message,
        paymentMethod: 'paddle',
        country: locale.code
      });

      alert(`Payment failed: ${error.message}. Please try again.`);
    }
  };

  // Helper functions
  const getTierName = (pricePerCredit) => {
    if (pricePerCredit === 2.00) return 'Standard';
    if (pricePerCredit === 1.60) return 'Bulk';
    if (pricePerCredit === 1.40) return 'Premium';
    if (pricePerCredit === 1.00) return 'Super Premium';
    return 'Unknown';
  };

  const calculateSavings = (quantity, pricePerCredit) => {
    const standardPrice = quantity * 2.00;
    const actualPrice = quantity * pricePerCredit;
    return Math.max(0, standardPrice - actualPrice);
  };

  // Don't render modal if not open
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Close modal when clicking backdrop (but not when clicking the modal content)
        if (e.target === e.currentTarget) {
          setIsProcessing(false);
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md sm:max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Purchase Credits</h2>
            <p className="text-sm text-gray-600 mt-1">
              {isSouthAfrica ? 'Variable Credit System' : 'Fixed Credit Packages'}
            </p>
            <p className="text-xs text-gray-500">
              {isSouthAfrica
                ? 'Buy exactly what you need • Bulk discounts available'
                : `Pay in ${locale.currency.code} • Local pricing for ${locale.name}`
              }
            </p>
          </div>
          <button
            onClick={() => {
              // Always allow modal to close via X button
              setIsProcessing(false);
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Variable Credit Purchase - All Countries */}
          <VariableCreditPurchase
            locale={locale}
            onPricingChange={handlePricingChange}
            disabled={isProcessing}
            paymentButton={
              <div>
                <button
                  onClick={isSouthAfrica ? handlePaystackPayment : handlePaddlePayment}
                  disabled={!pricingData?.isValid || isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-lg font-medium transition-all duration-200
                    ${pricingData?.isValid && !isProcessing
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }
                  `}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Loading payment form...
                    </div>
                  ) : pricingData?.isValid ? (
                    <div className="flex items-center justify-center">
                      <CreditCardIcon className="h-5 w-5 mr-2" />
                      Pay {pricingData.totalPrice ? formatPrice(pricingData.totalPrice, locale) : ''} with {paymentProvider}
                    </div>
                  ) : (
                    'Select credit amount'
                  )}
                </button>

                {/* Security Notice */}
                <div className="mt-2 flex items-center justify-center text-xs text-gray-500">
                  <ShieldCheckIcon className="h-4 w-4 mr-1" />
                  Secure payment powered by {paymentProvider}
                </div>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;